import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import useRequest from "@/lib/request/useRequest";
import {
    HarvestOperation,
    ListApplications,
    PlantingOperation,
    SectorSummary,
} from "@/types/operations";
import {
    faChartPie,
    faPencil,
    faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    Button,
    Card,
    CardBody,
    DateRangePicker,
    DateValue,
    Divider,
    RangeValue,
} from "@heroui/react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { parseDate } from "@internationalized/date";
import SummaryTable from "@/components/field/operations/SummaryTable";
import SectorTable from "@/components/field/operations/SectorTable";
import { roundTo } from "@/lib/utils/numbers";
export const Route = createFileRoute("/dashboard/fields/$fieldId/operations/")({
    component: RouteComponent,
});
import { useTranslation } from "react-i18next";
import useFeatureFlag from "@/lib/featureFlags/useFeatureFlags";
import { CropCycleV2 } from "@/types/cropCycle";
import { useI18n } from "@/lib/i18n/useI18n";
import useAuth from "@/lib/auth/useAuth";
import useConfirm from "@/lib/confirm/useConfirm";

function RouteComponent() {
    const { formatUnit } = useI18n();
    const { useGetRequest, useMutateRequest } = useRequest();
    const { cropCyclesV2Enabled } = useFeatureFlag();
    const { fieldId, selectedCropCycle } = useFieldDashboard() ?? {};
    const [viewSectorSummary, setViewSectorSummary] = useState(false);

    const { userIsSentinel } = useAuth();

    const harvestOperationsQuery = useGetRequest<HarvestOperation[]>({
        url: `fields/${fieldId}/harvest_operations/`,
        enabled: !!fieldId && !!selectedCropCycle?.start_date,
        params: {
            start_date: selectedCropCycle?.start_date,
            end_date: selectedCropCycle?.end_date,
        },
    });

    const plantingOperationsQuery = useGetRequest<PlantingOperation[]>({
        url: `fields/${fieldId}/planting_operations/`,
        enabled: !!fieldId && !!selectedCropCycle?.start_date,
        params: {
            start_date: selectedCropCycle?.start_date,
            end_date: selectedCropCycle?.end_date,
        },
    });

    const harvestOperation = useMemo(() => {
        if (
            cropCyclesV2Enabled &&
            (selectedCropCycle as CropCycleV2)?.effective_planting_operation
                ?.start_date
        ) {
            return harvestOperationsQuery?.data
                ?.filter(
                    (op) =>
                        op.start_date >=
                        (selectedCropCycle as CropCycleV2)
                            ?.effective_planting_operation?.start_date,
                )
                .sort((a, b) => (a.start_date > b.start_date ? -1 : 1))[0];
        }
        return harvestOperationsQuery?.data?.[0];
    }, [harvestOperationsQuery?.data, selectedCropCycle, cropCyclesV2Enabled]);

    const deleteHarvestOperation = useMutateRequest({
        url: harvestOperation
            ? `harvest_operations/${harvestOperation.id}/`
            : "",
        method: "delete",
    });

    const confirm = useConfirm();

    const handleHarvestDelete = () => {
        confirm.showConfirm({
            title: "Delete Operation",
            description: "Are you sure you want to delete this operation?",
            color: "danger",
            confirmText: "Delete",
            onConfirm: async () => {
                await deleteHarvestOperation.mutateAsync({});
                await harvestOperationsQuery.refetch?.();
            },
        });
    };

    const plantingOperation = useMemo(() => {
        if (
            cropCyclesV2Enabled &&
            (selectedCropCycle as CropCycleV2)?.planting_operation
        ) {
            return plantingOperationsQuery?.data?.find(
                (op) =>
                    op.id ===
                    (selectedCropCycle as CropCycleV2)?.planting_operation,
            );
        }
        return plantingOperationsQuery?.data?.[0];
    }, [plantingOperationsQuery?.data, selectedCropCycle, cropCyclesV2Enabled]);

    const [dates, setDates] = useState<RangeValue<DateValue> | null>({
        start: parseDate("2024-04-01"),
        end: parseDate("2024-04-08"),
    });

    useEffect(() => {
        if (!selectedCropCycle) return;

        setDates({
            start: parseDate(selectedCropCycle.start_date),
            end: selectedCropCycle.end_date
                ? parseDate(selectedCropCycle.end_date)
                : (null as unknown as DateValue),
        });
    }, [selectedCropCycle]);

    const appliationOperationsQuery = useGetRequest<ListApplications>({
        url: `fields/${fieldId}/application_operations/`,
        enabled: !!fieldId && !!selectedCropCycle?.start_date,
        params: {
            start_date: dates?.start,
            end_date: dates?.end,
        },
    });

    const sectorSummaryQuery = useGetRequest<SectorSummary[]>({
        url: `fields/${fieldId}/application_operations/sector_summary/`,
        enabled: !!fieldId && !!selectedCropCycle,
        params: {
            start_date: dates?.start,
            end_date: dates?.end,
        },
        queryOptions: {
            refetchOnWindowFocus: false,
            refetchOnMount: false,
            refetchOnReconnect: false,
        },
    });

    const { t } = useTranslation("operations");

    return (
        <div className="flex flex-col gap-2 p-2 text-sm">
            <Button
                fullWidth
                color="secondary"
                as={Link}
                to={"/dashboard/fields/$fieldId/operations/add"}
            >
                {t("addOperation")}
            </Button>
            {harvestOperation && (
                <>
                    <div className="flex flex-row items-center justify-between w-full">
                        <span className="text-brandgreen-500 font-semibold text-lg">
                            {t("harvest")}
                        </span>

                        <div className="flex items-center gap-1">
                            <Button
                                isIconOnly
                                variant="solid"
                                color="secondary"
                                as={Link}
                                size="sm"
                                to="/dashboard/fields/$fieldId/operations/harvest/$harvestOperationId"
                                params={
                                    {
                                        harvestOperationId: harvestOperation.id,
                                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                    } as any
                                }
                                aria-label="Edit Harvest Operation"
                            >
                                <FontAwesomeIcon icon={faPencil} />
                            </Button>
                            {userIsSentinel ? (
                                <Button
                                    isIconOnly
                                    variant="solid"
                                    color="danger"
                                    size="sm"
                                    aria-label="Delete Harvest Operation"
                                    onPress={handleHarvestDelete}
                                    isLoading={deleteHarvestOperation.isPending}
                                    isDisabled={
                                        deleteHarvestOperation.isPending
                                    }
                                >
                                    <FontAwesomeIcon icon={faTrash} />
                                </Button>
                            ) : null}
                        </div>
                    </div>

                    <Card>
                        <CardBody>
                            <div className="grid grid-cols-4 flex-1">
                                <div className="font-bold">{t("date")}</div>
                                <div className="font-bold">{t("crop")}</div>
                                <div className="font-bold">{t("yield")}</div>
                                <div className="font-bold">{t("moisture")}</div>
                                <div>{harvestOperation.start_date}</div>
                                <div>
                                    {plantingOperation?.crop_configuration
                                        ?.crop_name ?? "--"}
                                </div>
                                <div>
                                    {harvestOperation.irrigated_averages
                                        ?.yield ??
                                        harvestOperation.field_averages
                                            ?.yield}{" "}
                                    {t("yieldUnit")}
                                </div>
                                <div>
                                    {harvestOperation.irrigated_averages
                                        ?.moisture ??
                                        harvestOperation.field_averages
                                            ?.moisture}
                                    %
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </>
            )}
            {plantingOperation && (
                <>
                    <div className="flex flex-row items-center gap-1 justify-between w-full">
                        <span className="text-brandgreen-500 font-semibold text-lg">
                            {t("planting")}
                        </span>
                        <Button
                            isIconOnly
                            as={Link}
                            variant="solid"
                            color="secondary"
                            size="sm"
                            to="/dashboard/fields/$fieldId/operations/planting/$plantingOperationId"
                            params={
                                {
                                    plantingOperationId: plantingOperation.id,
                                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                } as any
                            }
                            aria-label="Edit Planting Operation"
                        >
                            <FontAwesomeIcon icon={faPencil} />
                        </Button>
                    </div>
                    <Card>
                        <CardBody>
                            <div className="grid grid-cols-4 flex-1">
                                <div className="font-bold">{t("date")}</div>
                                <div className="font-bold">{t("crop")}</div>
                                <div className="font-bold">{t("hybrid")}</div>
                                <div className="font-bold">
                                    {t("seedingRate")}
                                </div>
                                <div>{plantingOperation.start_date}</div>
                                <div>
                                    {plantingOperation?.crop_configuration
                                        ?.crop_name ?? "--"}
                                </div>
                                <div>
                                    {plantingOperation?.varieties?.[0]?.name ??
                                        "--"}
                                </div>
                                <div>
                                    {roundTo(
                                        plantingOperation.average_seeding_rate ??
                                            0,
                                        0,
                                    )}{" "}
                                    {`seeds/${formatUnit("ac", "ha")}`}
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </>
            )}
            <Divider />
            <DateRangePicker
                size="sm"
                label={t("startAndEndDate")}
                value={dates}
                aria-label={t("startAndEndDate")}
                onChange={setDates}
            />
            <div className="font-semibold text-lg flex justify-between items-center">
                {t("applications")}
                <Button
                    isIconOnly
                    variant="solid"
                    color="secondary"
                    aria-label="Sector Summary"
                    isLoading={sectorSummaryQuery.isLoading}
                    size="sm"
                    onPress={() => setViewSectorSummary(!viewSectorSummary)}
                >
                    <FontAwesomeIcon icon={faChartPie} />
                </Button>
            </div>
            {viewSectorSummary ? (
                <SectorTable sectorQuery={sectorSummaryQuery} />
            ) : (
                <SummaryTable operationsQuery={appliationOperationsQuery} />
            )}
        </div>
    );
}
